import React, { createContext, useContext, useEffect, useState } from 'react';
import { apiClient } from '@/lib/api-client';

export interface User {
  id: string;
  email: string;
  name: string;
  createdAt: string;
  emailVerified: boolean;
  status: 'waitlisted' | 'active';
  profile?: {
    age?: number;
    healthGoals?: string[];
    symptoms?: string[];
    preferences?: {
      notifications: boolean;
      theme: 'light' | 'dark';
      language: string;
    };
  };
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string; status?: 'waitlisted' | 'active' }>;
  logout: () => void;
  updateProfile: (profileData: Partial<User['profile']>) => Promise<boolean>;
  register: (email: string, password: string, displayName: string, timeZone: string) => Promise<{ success: boolean; error?: string }>;
}

type AuthPayload = {
  token?: string;
  user?: User;
};

type UserPayload = Partial<User> & {
  displayName?: string;
  fullName?: string;
  username?: string;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);
const isBrowser = typeof window !== 'undefined';

const getApiMessage = (message: string | undefined, fallback: string) => message || fallback;

const resolveDisplayName = (user: UserPayload): string => {
  const candidates = [user.name, user.displayName, user.fullName, user.username]
    .map((value) => value?.trim())
    .filter((value): value is string => Boolean(value));

  if (candidates.length > 0) {
    return candidates[0];
  }

  const emailPrefix = user.email?.split('@')[0]?.trim();
  return emailPrefix || 'User';
};

const normalizeUser = (user: UserPayload | undefined): User | null => {
  if (!user?.id || !user.email) {
    return null;
  }

  return {
    id: user.id,
    email: user.email,
    name: resolveDisplayName(user),
    createdAt: user.createdAt || new Date().toISOString(),
    emailVerified: user.emailVerified ?? false,
    status: (user as Partial<User>).status ?? 'waitlisted',
    profile: user.profile || {
      preferences: {
        notifications: true,
        theme: 'light',
        language: 'en',
      },
    },
  };
};

const storeAuth = (payload: AuthPayload) => {
  if (!isBrowser) {
    return;
  }

  if (payload.token) {
    localStorage.setItem('eirvana_token', payload.token);
  }
};

const clearAuth = () => {
  if (!isBrowser) {
    return;
  }

  localStorage.removeItem('eirvana_token');
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const loadCurrentUser = async () => {
      if (!isBrowser) {
        setUser(null);
        setIsLoading(false);
        return;
      }

      const token = localStorage.getItem('eirvana_token');

      if (!token) {
        setUser(null);
        setIsLoading(false);
        return;
      }

      const response = await apiClient.get<AuthPayload>('/api/me');

      if (cancelled) {
        return;
      }

      if (!response.success) {
        clearAuth();
        setUser(null);
        setIsLoading(false);
        return;
      }

      setUser(normalizeUser(response.data.user));
      setIsLoading(false);
    };

    void loadCurrentUser();

    return () => {
      cancelled = true;
    };
  }, []);

  const login = async (email: string, password: string) => {
    const response = await apiClient.post<AuthPayload>('/api/login', { email, password });

    if (!response.success) {
      clearAuth();
      setUser(null);
      return {
        success: false,
        error: getApiMessage(response.message, 'Invalid email or password.'),
      };
    }

    storeAuth(response.data);

    const normalized = normalizeUser(response.data.user);
    if (normalized) {
      setUser(normalized);
      return { success: true, status: normalized.status } as const;
    }

    // Some backends return token-only login responses; hydrate user from /api/me.
    const meResponse = await apiClient.get<AuthPayload>('/api/me');
    const meUser = meResponse.success ? normalizeUser(meResponse.data.user) : null;

    if (meUser) {
      setUser(meUser);
      return { success: true, status: meUser.status } as const;
    }

    clearAuth();
    setUser(null);
    return {
      success: false,
      error: 'Unable to load account details after login. Please try again.',
    };
  };

  const register = async (email: string, password: string, displayName: string, timeZone: string) => {
    const response = await apiClient.post<AuthPayload>('/api/register', {
      email,
      displayName,
      timeZone,
      password,
    });

    if (!response.success) {
      clearAuth();
      setUser(null);
      return {
        success: false,
        error: getApiMessage(response.message, 'Failed to join the waitlist. Please try again.'),
      };
    }

    storeAuth(response.data);
    setUser(normalizeUser(response.data.user));
    return { success: true };
  };

  const logout = () => {
    void apiClient.post('/api/logout');
    clearAuth();
    setUser(null);
  };

  const updateProfile = async (profileData: Partial<User['profile']>): Promise<boolean> => {
    const response = await apiClient.put<AuthPayload>('/api/profile', {
      profile: profileData,
    });

    if (!response.success) {
      return false;
    }

    const updatedUser = normalizeUser(response.data.user);

    if (updatedUser) {
      setUser(updatedUser);
      return true;
    }

    if (user) {
      setUser({
        ...user,
        profile: {
          ...user.profile,
          ...profileData,
        },
      });
      return true;
    }

    return false;
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    updateProfile,
    register,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const useOptionalAuth = (): AuthContextType | null => {
  const context = useContext(AuthContext);
  return context ?? null;
};
